import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Tooltip from "@mui/material/Tooltip";
import { deleteUserById, getUsers } from "../../Services/api";
import { ToastContainer, toast } from "react-toastify";
import {
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

const User = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const navigate = useNavigate();

  const getUsersData = async () => {
    setLoading(true);
    try {
      const result = await getUsers();
      setData(result);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users. Please try again.", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteUserById(userToDelete._id);
      toast.success(result.message, { position: "top-center" });
      getUsersData();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user. Please try again.", {
        position: "top-center",
      });
    } finally {
      setDeleteLoading(false);
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  useEffect(() => {
    getUsersData();
  }, []);

  const handleEdit = (rowData) => {
    navigate(`/user-form/${rowData.name}`, { state: { user: rowData } });
  };

  const handleDeleteClick = (rowData) => {
    setUserToDelete(rowData);
    setOpenDeleteDialog(true);
  };

  const handleAdd = () => {
    navigate("/user-form");
  };

  const columns = [
    {
      name: "name",
      label: "Name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "mobile",
      label: "Mobile",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "cast",
      label: "Caste",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "gender",
      label: "Gender",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "age",
      label: "Age",
      options: {
        customBodyRender: (value, tableMeta) => {
          const dob = new Date(data[tableMeta.rowIndex].dob);
          const age = new Date().getFullYear() - dob.getFullYear();
          return age;
        },
        filter: true,
        sort: true,
      },
    },
    {
      name: "marital_status",
      label: "Marital Status",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const userData = data[tableMeta.rowIndex];
          return (
            <div>
              <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(userData)}>
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton onClick={() => handleDeleteClick(userData)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    responsive: "standard",
    print: false,
    download: false,
    viewColumns: false,
    filter: true,
    selectableRows: "none",
    customToolbar: () => (
      <Tooltip title="Add User">
        <IconButton onClick={handleAdd}>
          <ControlPointIcon color="primary" />
        </IconButton>
      </Tooltip>
    ),
    textLabels: {
      body: {
        noMatch: loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
  };

  return (
    <>
      <ToastContainer autoClose={3000} />

      <MUIDataTable
        title={"All Users List"}
        data={data}
        columns={columns}
        options={options}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete user{" "}
          <strong>{userToDelete?.name}</strong>?
          <br />
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            color="primary"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default User;
